import { useEffect, useRef, useState } from 'react'
import api from '@/lib/api'
import { cn } from '@/lib/utils'
import { ChevronsUpDown } from 'lucide-react'

/**
 * Combobox com busca assíncrona no backend (ideal para FKs grandes,
 * ex.: municípios, tributação nacional). Busca em /endpoint?q=...
 */
export default function AsyncSelect({
  endpoint,
  value,
  onChange,
  optionValue = 'id',
  optionLabel = 'nome',
  placeholder = 'Selecione...',
  initialLabel = '',
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState([])
  const [label, setLabel] = useState(initialLabel)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef(null)

  useEffect(() => setLabel(initialLabel), [initialLabel])

  // Fecha ao clicar fora.
  useEffect(() => {
    function onClick(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Busca com debounce quando aberto.
  useEffect(() => {
    if (!open) return
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await api.get(endpoint, { params: { q: query, limit: 20 } })
        setOptions(data.data || [])
      } finally {
        setLoading(false)
      }
    }, 250)
    return () => clearTimeout(t)
  }, [query, open, endpoint])

  function choose(opt) {
    onChange(opt[optionValue])
    setLabel(opt[optionLabel])
    setOpen(false)
    setQuery('')
  }

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-border bg-input px-3 py-2 text-sm',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <span className={cn(!label && 'text-muted-foreground')}>{label || placeholder}</span>
        <ChevronsUpDown className="h-4 w-4 opacity-50" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar..."
            className="w-full px-3 py-2 text-sm border-b border-border bg-transparent focus:outline-none"
          />
          <ul className="max-h-56 overflow-auto py-1">
            {loading && <li className="px-3 py-2 text-sm text-muted-foreground">Buscando...</li>}
            {!loading && options.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">Nenhum resultado.</li>
            )}
            {options.map((opt) => (
              <li key={opt[optionValue]}>
                <button
                  type="button"
                  onClick={() => choose(opt)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent/60"
                >
                  {opt[optionLabel]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
